import type { BadgeColors } from "@/components/base/badges/badge-types";
import { Badge } from "@/components/base/badges/badges";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";

export type Pill = { key: string; label: string; color?: BadgeColors };

/**
 * Pills that stop at `max` and fold the rest into a "+N" pill, whose tooltip
 * lists every one. Workspaces and merchants both use it so they collapse alike.
 */
export const PillList = ({
  pills,
  max,
  noun,
  nowrap,
  className,
}: {
  pills: Pill[];
  max: number;
  /** What the pills are, for the overflow's tooltip title and label. */
  noun: { one: string; many: string };
  /** Keeps the pills on one line, where a wrapped "+N" would land on a line of its own. */
  nowrap?: boolean;
  className?: string;
}) => {
  const visible = pills.slice(0, max);
  const hidden = pills.length - visible.length;
  return (
    <span
      className={cx(
        "flex min-w-0 items-center gap-1.5",
        nowrap ? "flex-nowrap overflow-hidden" : "flex-wrap",
        className,
      )}
    >
      {visible.map((pill) => (
        // On one line a pill gives way to the text beside it rather than overlapping it.
        <Badge key={pill.key} size="sm" color={pill.color ?? "brand"} className={nowrap ? "min-w-0 shrink" : undefined}>
          <span className={nowrap ? "truncate" : undefined}>{pill.label}</span>
        </Badge>
      ))}
      {hidden > 0 && (
        // Inside a selectable row, opening the list must not also select the row.
        // biome-ignore lint/a11y/noStaticElementInteractions: only stops the click reaching the row
        // biome-ignore lint/a11y/useKeyWithClickEvents: the trigger inside is the keyboard target
        <span className="shrink-0" onClick={(event) => event.stopPropagation()}>
          <Tooltip
            placement="top"
            title={noun.many[0].toUpperCase() + noun.many.slice(1)}
            description={
              <ul className="flex flex-col gap-0.5">
                {pills.map((pill) => (
                  <li key={pill.key}>{pill.label}</li>
                ))}
              </ul>
            }
          >
            <TooltipTrigger
              aria-label={`${hidden} more ${hidden === 1 ? noun.one : noun.many}`}
              className="inline-flex cursor-default items-center rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200 ring-inset"
            >
              +{hidden}
            </TooltipTrigger>
          </Tooltip>
        </span>
      )}
    </span>
  );
};
